import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';

export enum CampaignLeadSource {
  csv = 'csv',
  hubspot = 'hubspot',
  apollo = 'apollo',
  eigen = 'eigen',
}

registerEnum({ name: 'CampaignLeadSource', enum: CampaignLeadSource });

@Entity()
export class ReplyMessageDoc extends MongoEntity {
  static override _class = 'ReplyMessageDoc';

  @Field(type => String) text!: string;
  @Field(type => String) timestamp!: string;
}

@Entity()
export class CampaignLead extends MongoEntity {
  static override _class = 'CampaignLead';
  static override _collectionName = 'campaign_leads';

  @Field(type => String) campaignId!: string;
  @Field(type => String) userId!: string;

  @Field(type => String) firstName!: string;
  @Field(type => String) lastName!: string;
  @Field(type => String) email!: string;
  @Field(type => String) company!: string;
  @Field(type => String) jobTitle!: string;
  @Field(type => String) linkedinUrl?: string;
  @Field(type => String) industry?: string;
  @Field(type => String) phone?: string;
  @Field(type => String) companyLocation?: string;
  @Field(type => String) employeeCount?: string;
  @Field(type => String) revenue?: string;
  @Field(type => String) department?: string;
  @Field(type => String) personLocation?: string;
  @Field(type => String) eigenLeadId?: string;

  /**
   * Apollo person id for leads sourced via Apollo People Search.
   * Used to call Apollo's Bulk People Enrichment at launch to get unobfuscated
   * names, emails, phones, etc.
   */
  @Field(type => String) apolloPersonId?: string;

  /**
   * ISO timestamp — when this lead was successfully enriched via Apollo's
   * Bulk People Enrichment endpoint. Absent means the row was sourced but
   * never enriched (e.g. campaign not launched yet, or Apollo returned no
   * match for the person id).
   */
  @Field(type => String) apolloEnrichedAt?: string;

  /** When imported from a CRM list (e.g. HubSpot), the existing CRM contact id. */
  @Field(type => String) crmContactId?: string;
  /** Origin of this lead row — 'csv' | 'hubspot' | 'apollo' | 'eigen' . */
  @Field(type => CampaignLeadSource) source?: CampaignLeadSource;

  @Field(type => String) status!: string; // 'pending' | 'contacted' | 'replied' | 'bounced' | 'opted_out'
  /**
   * Operator's MANUAL status override (Manual Overwrite). Absent when the lead's
   * state is left to the automation. When set it WINS the status chip (except a
   * global Do-Not-Contact suppression, which always shows), so it survives a
   * reload rather than being masked by a leftover timestamp or overwritten by the
   * sync reconciler — neither of which touches this field. A terminal override
   * (replied/bounced/opted_out) also halts the lead's remaining outreach at the
   * point it is set (see routes/campaign-leads.ts). One of the stored vocabulary:
   * 'pending' | 'contacted' | 'replied' | 'bounced' | 'opted_out'.
   */
  @Field(type => String) manualStatus?: string;
  /**
   * When the operator DISMISSED the "already connected — no DM was sent
   * automatically" notice on this lead. Absent means the notice still shows.
   * Once set the notice stays hidden across reloads — the operator has seen it
   * and does not want the reminder again. Display-only; nothing on a send path
   * reads it.
   */
  @Field(type => String) alreadyConnectedNoticeDismissedAt?: string;
  @Field(type => Int) currentStep!: number;
  @Field(type => String) lastContactedAt?: string;

  @Field(type => Int) instantlyLeadStatus?: number; // 0=not_contacted..7=completed
  @Field(type => String) sentAt?: string;
  @Field(type => String) openedAt?: string;
  @Field(type => String) repliedAt?: string;
  @Field(type => String) bouncedAt?: string;
  /**
   * The far side's verbatim explanation of why the address failed, e.g.
   * `smtp; 550 5.1.1 The email account that you tried to reach does not exist`.
   *
   * Kept for the same reason a job keeps the provider's refusal: "bounced" is a
   * conclusion, and the sentence it was drawn from is the only thing that
   * answers the next question — a typo in the address, a mailbox that closed,
   * or our own domain being rejected, which is a different emergency.
   */
  @Field(type => String) bounceEvidence?: string;
  @Field(type => String) replyText?: string;

  /** All individual reply messages with timestamps (populated during sync for full conversation context) */
  @Field(type => [ReplyMessageDoc]) replyMessages?: ReplyMessageDoc[];

  // ── LinkedIn connection ──────────────────────────────────────────────────
  // `acceptedAt`, `alreadyConnected`, `invitePendingElsewhere` and
  // `connectionSentAt` used to live here. They are gone on purpose.
  //
  // They were copies of facts already recorded in LinkedinObservation (what the
  // provider said) and OutreachJob (what we did), and a copy is the only way a
  // contradiction becomes representable: as long as `acceptedAt` was a column,
  // it could disagree with `connectionState`, and a single writer merely
  // narrowed the list of suspects. They are now COMPUTED on read — see
  // helpers/linkedin-projection.ts — under the same names, so nothing outside
  // the server noticed. A database validator rejects any attempt to write them
  // back (helpers/linkedin-db-constraints.ts).

  /**
   * THE authoritative connection state (ConnectionState in
   * linkedin-vocabulary.ts). The only stored fact about this lead's LinkedIn
   * relationship.
   *
   * Only linkedin-connection.ts may write it, and only from a provider
   * response — see the header of that module for why.
   */
  @Field(type => String) connectionState?: string;
  /**
   * The sending account `connectionState` was observed from. A connection is a
   * property of the PAIR (lead, account); a state observed from a different
   * account answers a different question. Campaigns here use several sender
   * accounts, and nothing used to record which one a state came from.
   */
  @Field(type => String) connectionAccountId?: string;
  /** ISO timestamp of the last live provider check. Also the compare-and-set key. */
  @Field(type => String) connectionCheckedAt?: string;
  /** Which observation produced the current state, e.g. `profile_check:THIRD_DEGREE`. */
  @Field(type => String) connectionEvidence?: string;

  @Field(type => String) dmSentAt?: string;
  /**
   * LinkedIn's own id for this person, resolved once and kept.
   *
   * The sync used to resolve it on every run for every lead whose chat could
   * not be matched by name — one Unipile profile lookup each, in series. On a
   * hundred-lead campaign where almost nobody has replied that is ~99 lookups
   * per sync, and the UI auto-syncs every sixty seconds: roughly six thousand
   * profile requests an hour, made through the very LinkedIn accounts the
   * campaign sends from. It timed out the request AND is exactly the behaviour
   * an account gets throttled for.
   *
   * A person's provider id does not change, so it is looked up at most once.
   */
  @Field(type => String) linkedinProviderId?: string;

  /** ISO timestamp — when this lead's sequence was determined to be complete */
  @Field(type => String) doneAt?: string;

  /** True when there is at least one ReplyDraft with status 'pending_review' for this lead */
  @Field(type => Boolean) hasPendingDraft?: boolean;

  /** Per-lead override for the campaign replyPrompt */
  @Field(type => String) replyPromptOverride?: string;

  /** Which email account originally sent the outreach to this lead */
  @Field(type => String) sentFromEmailAccount?: string;

  /** Which LinkedIn account originally sent the connection/DM to this lead */
  @Field(type => String) sentFromLinkedinAccountId?: string;

  /**
   * The provider's id of the FIRST email sent to this lead in the sequence, kept
   * so every follow-up threads onto the same conversation. Unipile's send
   * returns this `provider_id` (docs/unipile-email-api.md); passing it back as
   * `reply_to` is what makes a sequence arrive as one thread rather than three
   * unrelated messages. Set once, on the first send; read by every later step.
   */
  @Field(type => String) emailThreadProviderId?: string;

  /**
   * The subject the conversation with this lead opened under, kept so every
   * follow-up replies under it rather than under one of its own.
   *
   * `emailThreadProviderId` says WHICH message is being answered; this says what
   * the answer is called, and both are needed. A follow-up sent under a freshly
   * generated subject was refused live on 17 August (`422
   * errors/invalid_reply_subject`), and even where a provider accepts it, Gmail
   * and Outlook group by subject — so it arrives as a separate message and the
   * threading the `reply_to` was for is lost anyway.
   */
  @Field(type => String) emailThreadSubject?: string;

  /**
   * The RFC 2822 Message-IDs of the emails we have sent to this lead, angle
   * brackets stripped and lower-cased (helpers/email-reply.ts `normalizeMessageId`).
   *
   * Harvested from the SENT-folder echo Unipile sends back for our own outbound
   * mail — the one delivery that carries the message-id and the recipient
   * together. A bounce (`email.new.bounce`) carries ONLY this message-id: never
   * our `emailThreadProviderId` (which is Gmail's hex `provider_id`, a different
   * namespace) and never a usable recipient (its `addresses` arrives truncated).
   * So this is the only bridge from a bounce back to the lead — see
   * `interpretNativeBounce` in routes/webhooks.ts.
   */
  @Field(type => [String]) sentEmailMessageIds?: string[];

  /**
   * Sales Rep assigned to this lead (used for persona on outreach + reply drafts
   * and for sub-campaign routing). Set during prepare. Falls back to campaign.aiEmployeeId.
   */
  @Field(type => String) aiEmployeeId?: string;
}
